import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { BodyClassService } from '@app/shared/services/body-class.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { CountryDataService } from '@app/shared/location/country-data.service';
import { GaValidateService } from '@app/shared/formly/ga-validate.service';
import { AuthService } from '@app/shared/services/auth/auth.service';
import _ from 'underscore';
import { EDITOR_TINYMCE, TextEditor, TINYMCE_SIMPLE } from '@app/shared/editor/editor-config.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { ITicketCategory, ITicketCreationContext, TicketDataService } from '@app/shared/ticket/ticket-data.service';

const WORKFLOW_TYPE_PO:string = 'TODO_PO';

@Component({
  selector: 'create-ticket',
  template: `
    <div class="create-ticket">
      <ng-container *ngIf="!ticketCreated">
        <formly [form]="form" [fields]="fields" [model]="model" [noValidate]="true">
          <button class="btn" (click)="submitTicket()" [busyButton]="busy" translate>GA.BUTTONS.SUBMIT</button>
        </formly>
      </ng-container>

      <div class="alert alert-success" *ngIf="ticketCreated">
        <i class="fa fa-check-circle"></i>
        <span translate>APP.TICKET.MESSAGE.SUCCESS_EXTERNAL</span>
      </div>
    </div>
  `,
  styleUrls: ['./create-ticket.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CreateTicketComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() public context:string;
  @Input() public requestType:string;

  public form:UntypedFormGroup = new UntypedFormGroup({});
  public model:ITicketCreationContext;
  public fields:IExtraFieldConfigurationObject[];
  public busy:boolean;
  public ticketCreated:boolean;
  // define editor to be used here, so we can change to normal textarea from unit test
  public editor:TextEditor = EDITOR_TINYMCE;

  private countryField:IExtraFieldConfigurationObject;
  private poNumberField:IExtraFieldConfigurationObject;
  private serialNumberField:IExtraFieldConfigurationObject;
  private categoryField:IExtraFieldConfigurationObject;

  constructor(
    private bodyClassService:BodyClassService,
    private formlyService:GaFormlyService,
    private translationService:TranslationService,
    private formattingService:FormattingService,
    private countryDataService:CountryDataService,
    private validateService:GaValidateService,
    private authService:AuthService,
    private ticketDataService:TicketDataService
  ) {
    bodyClassService.add('create-ticket');
  }

  public ngOnInit():void {
    // clear model
    this.clearModel();
    this.fields = this.createFields();

    // fill formly options fields
    this.fillFields();

    // get ticket context data and set it to model
    this.ticketDataService.getTicketContext(this.context)
      .then((res:ITicketCreationContext) => {
        this.form.patchValue(res);
      });
  }

  public ngAfterViewInit():void {
    // update serialNumber field validation on poNumber model change
    this.poNumberField.formControl.valueChanges.subscribe(() => {
      this.serialNumberField.formControl.updateValueAndValidity({ emitEvent: false });
    });

    // update poNumber field validation on serialNumber model change
    this.serialNumberField.formControl.valueChanges.subscribe(() => {
      this.poNumberField.formControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  public ngOnDestroy():void {
    this.bodyClassService.remove('create-ticket');
  }

  private fillFields():void {
    this.formlyService.fillField(this.countryField, this.countryDataService.getAllCountries());
    this.formlyService.fillField(this.categoryField, this.ticketDataService.getCategories(undefined, WORKFLOW_TYPE_PO))
      .then((categories:ITicketCategory[]) => {
        // if request type set then try to check if exist within categoryType of categories list
        const preSelectedCategory:ITicketCategory = _.findWhere(categories, { categoryType: this.requestType && this.requestType.toUpperCase() });

        if (preSelectedCategory) {
          this.form.patchValue({ category: preSelectedCategory });
        }
      });
  }

  private clearModel():void {
    this.model = {
      email: this.authService.getCurrentUser()?.email,
      firstName: this.authService.getCurrentUser()?.firstname,
      lastName: this.authService.getCurrentUser()?.lastname,
      company: undefined,
      country: this.authService.getCurrentUser()?.location?.companyCountry?.country,
      subject: undefined,
      description: undefined,
      poNumber: undefined,
      serialNumber: undefined,
      category: undefined
    };

    this.form.reset(this.model);
  }

  private validateFields():Promise<void|string[]> {
    return this.validateService.validateFields(this.fields, this.model, true);
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const labelClass:string = 'col-sm-3';
    const valueClass:string = 'col-sm-9';

    const validationKey:string = 'serialNumberOrPONumber';
    const validationMessage:string = this.translationService.instant('APP.TICKET.PO_SN_VALIDATION');

    return [
      this.formlyService.anInput('email', 'GA.GENERAL.EMAIL')
        .isRequired()
        .withInputType('email')
        .withInfo('APP.ACCESS.SIGNUP.TIP_USE_WORK_EMAIL')
        .showInfo()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),
      this.formlyService.anInput('firstName', 'GA.GENERAL.FIRSTNAME')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),
      this.formlyService.anInput('lastName', 'GA.GENERAL.LASTNAME')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),
      this.formlyService.aValue('company', 'GA.GENERAL.COMPANY')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat(this.formattingService.formatCompany)
        .build(),
      this.countryField = this.formlyService.aSelect('country', 'GA.GENERAL.COUNTRY')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat(this.formattingService.formatCountry)
        .build(),
      this.poNumberField = this.formlyService.anInput('poNumber', 'APP.TICKET.PONUMBER.LABEL')
        .isNotRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withValidationMessage(validationKey, validationMessage)
        .withExtraOptions({
          validators: {
            [ validationKey ]: (control:UntypedFormControl) => {
              return !!(control.value || this.model.serialNumber);
            }
          }
        })
        .build(),
      this.serialNumberField = this.formlyService.anInput('serialNumber', 'APP.TICKET.SERIALNUMBER.LABEL')
        .isNotRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withValidationMessage(validationKey, validationMessage)
        .withExtraOptions({
          validators: {
            [ validationKey ]: (control:UntypedFormControl) => {
              return !!(control.value || this.model.poNumber);
            }
          }
        })
        .build(),
      this.categoryField = this.formlyService.aSelect('category', 'APP.TICKET.FORMLABEL.REQUESTTYPE')
        .isRequired()
        .withFormat(this.formattingService.formatTicketCategory)
        .withExtraOptions({
          templateOptions: {
            trackBy: 'categoryType'
          }
        })
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),
      this.formlyService.anInput('subject', 'APP.TICKET.FORMLABEL.DESCRIPTION')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withMaxLength(100)
        .build(),
      this.formlyService.aTextarea('description', 'APP.TICKET.FORMLABEL.COMMENTS')
        .isRequired()
        .withLabelClass('col-sm-12')
        .withValueClass('col-sm-12')
        .withExtraOptions({
          templateOptions: {
            editor: this.editor,
            editorCode: TINYMCE_SIMPLE
          }
        })
        .build()
    ];
  }

  public submitTicket():void {
    this.busy = true;
    this.validateFields()
      .then(() => {
        return this.ticketDataService.createExternalTicket(this.context, _.clone(this.model));
      })
      .then(() => {
        this.ticketCreated = true;
        this.clearModel();
      })
      .finally(() => {
        this.busy = false;
      });
  }
}